import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase';
import smsService from './smsService';
import { customerService } from './customerService';

// Coleções
const REWARDS_COLLECTION = 'rewards';
const REDEMPTIONS_COLLECTION = 'redemptions';
const REWARDS_HISTORY_COLLECTION = 'rewards_history';

/**
 * HISTÓRICO DE MOVIMENTAÇÕES
 */

// Registrar movimentação de estoque
export const addRewardMovement = async (companyId, movementData) => {
  try {
    const movement = {
      companyId,
      rewardId: movementData.rewardId,
      rewardName: movementData.rewardName,
      type: movementData.type, // 'stock_add', 'stock_remove', 'redemption', 'adjustment'
      quantity: movementData.quantity,
      previousStock: movementData.previousStock,
      newStock: movementData.newStock,
      batchCode: movementData.batchCode || null,
      reason: movementData.reason || '',
      notes: movementData.notes || '',
      userId: movementData.userId || null,
      userName: movementData.userName || null,
      redemptionId: movementData.redemptionId || null,
      createdAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, REWARDS_HISTORY_COLLECTION), movement);
    return { id: docRef.id, ...movement };
  } catch (error) {
    console.error('Erro ao registrar movimentação:', error);
    throw new Error('Falha ao registrar movimentação: ' + error.message);
  }
};

// Buscar histórico de movimentações
export const getRewardMovements = async (companyId, filters = {}) => {
  try {
    let q = query(
      collection(db, REWARDS_HISTORY_COLLECTION),
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc')
    );

    // Aplicar filtros se fornecidos
    if (filters.rewardId) {
      q = query(q, where('rewardId', '==', filters.rewardId));
    }
    
    if (filters.type) {
      q = query(q, where('type', '==', filters.type));
    }

    if (filters.batchCode) {
      q = query(q, where('batchCode', '==', filters.batchCode));
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Erro ao buscar histórico de movimentações:', error);
    throw new Error('Falha ao buscar histórico: ' + error.message);
  }
};

/**
 * SERVIÇOS DE PRÊMIOS
 */

// Criar um novo prêmio
export const createReward = async (companyId, rewardData) => {
  try {
    const reward = {
      companyId,
      name: rewardData.name,
      description: rewardData.description,
      pointsRequired: parseInt(rewardData.pointsRequired),
      type: rewardData.type, // 'product', 'cashback', 'discount', 'service'
      value: rewardData.value || '', // Valor em R$ para cashback ou descrição do produto
      imageUrl: rewardData.imageUrl || null, // URL da imagem do prêmio
      imagePath: rewardData.imagePath || null, // Caminho da imagem no Storage
      // Controle de estoque - para produtos e aluguéis
      stockQuantity: (rewardData.type === 'product' || rewardData.type === 'rental') ? parseInt(rewardData.stockQuantity) || 0 : 0,
      initialStock: (rewardData.type === 'product' || rewardData.type === 'rental') ? parseInt(rewardData.stockQuantity) || 0 : 0,
      hasStockControl: (rewardData.type === 'product' || rewardData.type === 'rental') ? (rewardData.hasStockControl !== false) : false,
      lowStockThreshold: (rewardData.type === 'product' || rewardData.type === 'rental') ? (parseInt(rewardData.lowStockThreshold) || 5) : 0,
      // Código do lote - para produtos e aluguéis com controle de estoque
      batchCode: ((rewardData.type === 'product' || rewardData.type === 'rental') && rewardData.hasStockControl && rewardData.batchCode) ? rewardData.batchCode.trim() : null,
      // Campos específicos de aluguel
      rentalDuration: rewardData.type === 'rental' ? parseInt(rewardData.rentalDuration) || 7 : null,
      isActive: true,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, REWARDS_COLLECTION), reward);
    return { id: docRef.id, ...reward };
  } catch (error) {
    console.error('Erro ao criar prêmio:', error);
    throw new Error('Falha ao criar prêmio');
  }
};

// Buscar prêmio por ID
export const getRewardById = async (rewardId) => {
  try {
    const rewardRef = doc(db, REWARDS_COLLECTION, rewardId);
    const rewardDoc = await getDoc(rewardRef);
    
    if (!rewardDoc.exists()) {
      throw new Error('Prêmio não encontrado');
    }

    return {
      id: rewardDoc.id,
      ...rewardDoc.data()
    };
  } catch (error) {
    console.error('Erro ao buscar prêmio:', error);
    throw new Error('Falha ao buscar prêmio');
  }
};

// Buscar prêmios de uma empresa
export const getCompanyRewards = async (companyId, activeOnly = true) => {
  try {
    // Validação básica
    if (!companyId) {
      throw new Error('Company ID é obrigatório');
    }

    // Query básica por companyId
    const q = query(
      collection(db, REWARDS_COLLECTION),
      where('companyId', '==', companyId)
    );

    const querySnapshot = await getDocs(q);
    let rewards = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Filtrar por status ativo se necessário
    if (activeOnly) {
      rewards = rewards.filter(reward => reward.isActive === true);
    }

    // Ordenar por pontos necessários
    rewards.sort((a, b) => a.pointsRequired - b.pointsRequired);

    // Adicionar informações de estoque
    return rewards.map(reward => {
      const isLowStock = (reward.type === 'product' || reward.type === 'rental') && reward.hasStockControl && reward.stockQuantity < reward.lowStockThreshold;
      
      return {
        ...reward,
        // Garantir que campos de estoque existam
        stockQuantity: reward.stockQuantity || 0,
        hasStockControl: (reward.type === 'product' || reward.type === 'rental') ? (reward.hasStockControl !== false) : false,
        lowStockThreshold: reward.lowStockThreshold || 5,
        initialStock: reward.initialStock || reward.stockQuantity || 0,
        // Status do estoque
        isOutOfStock: (reward.type === 'product' || reward.type === 'rental') && reward.hasStockControl && reward.stockQuantity <= 0,
        isLowStock: isLowStock,
        stockStatus: (reward.type !== 'product' && reward.type !== 'rental') ? 'not_applicable' :
                    !reward.hasStockControl ? 'unlimited' : 
                    reward.stockQuantity <= 0 ? 'out_of_stock' :
                    isLowStock ? 'low_stock' : 'available'
      };
    });

    return rewards;
  } catch (error) {
    console.error('Erro ao buscar prêmios:', error);
    throw new Error('Falha ao buscar prêmios: ' + error.message);
  }
};

// Atualizar prêmio
export const updateReward = async (rewardId, updateData) => {
  try {
    const rewardRef = doc(db, REWARDS_COLLECTION, rewardId);
    const updatedData = {
      ...updateData,
      updatedAt: Timestamp.now()
    };

    await updateDoc(rewardRef, updatedData);
    return { id: rewardId, ...updatedData };
  } catch (error) {
    console.error('Erro ao atualizar prêmio:', error);
    throw new Error('Falha ao atualizar prêmio');
  }
};

// Atualizar estoque de um prêmio
export const updateRewardStock = async (rewardId, newQuantity, batchCode, userId = null, userName = null) => {
  try {
    const rewardRef = doc(db, REWARDS_COLLECTION, rewardId);
    const rewardDoc = await getDoc(rewardRef);
    
    if (!rewardDoc.exists()) {
      throw new Error('Prêmio não encontrado');
    }

    const rewardData = rewardDoc.data();
    const previousStock = rewardData.stockQuantity || 0;
    const newStock = parseInt(newQuantity);
    
    const updatedData = {
      stockQuantity: newStock,
      updatedAt: Timestamp.now()
    };

    // Adicionar código do lote se fornecido
    if (batchCode && batchCode.trim()) {
      updatedData.batchCode = batchCode.trim();
      updatedData.lastBatchUpdate = Timestamp.now();
    }

    await updateDoc(rewardRef, updatedData);

    // Registrar movimentação no histórico
    try {
      const movementType = newStock > previousStock ? 'stock_add' : 'stock_remove';
      const quantityDiff = Math.abs(newStock - previousStock);
      
      await addRewardMovement(rewardData.companyId, {
        rewardId,
        rewardName: rewardData.name,
        type: movementType,
        quantity: quantityDiff,
        previousStock,
        newStock,
        batchCode: batchCode || rewardData.batchCode,
        reason: newStock > previousStock ? 'Adição de estoque' : 'Redução de estoque',
        notes: `Estoque atualizado de ${previousStock} para ${newStock}`,
        userId,
        userName
      });
    } catch (historyError) {
      console.error('Erro ao registrar histórico:', historyError);
      // Não falhar a operação principal se o histórico der erro
    }

    return { id: rewardId, stockQuantity: newStock, batchCode };
  } catch (error) {
    console.error('Erro ao atualizar estoque do prêmio:', error);
    throw new Error('Falha ao atualizar estoque do prêmio');
  }
};

// Adicionar estoque a um prêmio
export const addRewardStock = async (rewardId, quantity) => {
  try {
    const rewardRef = doc(db, REWARDS_COLLECTION, rewardId);
    const rewardDoc = await getDoc(rewardRef);
    
    if (!rewardDoc.exists()) {
      throw new Error('Prêmio não encontrado');
    }

    const currentStock = rewardDoc.data().stockQuantity || 0;
    const newStock = currentStock + parseInt(quantity);

    await updateDoc(rewardRef, {
      stockQuantity: newStock,
      updatedAt: Timestamp.now()
    });

    return { id: rewardId, oldStock: currentStock, newStock };
  } catch (error) {
    console.error('Erro ao adicionar estoque:', error);
    throw new Error('Falha ao adicionar estoque: ' + error.message);
  }
};

// Reduzir estoque (usado durante resgates)
export const reduceRewardStock = async (rewardId, quantity = 1, redemptionId = null, customerName = null, batchCode = null) => {
  try {
    const rewardRef = doc(db, REWARDS_COLLECTION, rewardId);
    const rewardDoc = await getDoc(rewardRef);
    
    if (!rewardDoc.exists()) {
      throw new Error('Prêmio não encontrado');
    }

    const reward = rewardDoc.data();
    const currentStock = reward.stockQuantity || 0;

    // Verificar se tem controle de estoque
    if (reward.hasStockControl !== false) {
      if (currentStock < quantity) {
        throw new Error('Estoque insuficiente para este prêmio');
      }
    }

    const newStock = Math.max(0, currentStock - parseInt(quantity));

    await updateDoc(rewardRef, {
      stockQuantity: newStock,
      updatedAt: Timestamp.now()
    });

    // Registrar movimentação no histórico
    try {
      await addRewardMovement(reward.companyId, {
        rewardId,
        rewardName: reward.name,
        type: 'redemption',
        quantity: parseInt(quantity),
        previousStock: currentStock,
        newStock,
        batchCode: batchCode || reward.batchCode,
        reason: 'Resgate de prêmio',
        notes: customerName ? `Resgatado por: ${customerName}` : 'Resgate processado',
        redemptionId
      });
    } catch (historyError) {
      console.error('Erro ao registrar histórico de resgate:', historyError);
      // Não falhar a operação principal se o histórico der erro
    }

    return { id: rewardId, oldStock: currentStock, newStock, reduced: quantity };
  } catch (error) {
    console.error('Erro ao reduzir estoque:', error);
    throw new Error('Falha ao reduzir estoque: ' + error.message);
  }
};

// Verificar se prêmio tem estoque disponível
export const checkRewardStock = async (rewardId, quantityNeeded = 1) => {
  try {
    const rewardRef = doc(db, REWARDS_COLLECTION, rewardId);
    const rewardDoc = await getDoc(rewardRef);
    
    if (!rewardDoc.exists()) {
      throw new Error('Prêmio não encontrado');
    }

    const reward = rewardDoc.data();
    
    // Se não tem controle de estoque, sempre disponível
    if (reward.hasStockControl === false) {
      return { available: true, hasStock: true, stockQuantity: 'unlimited' };
    }

    const currentStock = reward.stockQuantity || 0;
    const hasStock = currentStock >= quantityNeeded;

    return {
      available: hasStock,
      hasStock,
      stockQuantity: currentStock,
      quantityNeeded,
      isLowStock: currentStock < (reward.lowStockThreshold || 5)
    };
  } catch (error) {
    console.error('Erro ao verificar estoque:', error);
    throw new Error('Falha ao verificar estoque: ' + error.message);
  }
};

// Buscar lotes disponíveis de um prêmio
export const getRewardBatches = async (rewardId) => {
  try {
    const rewardRef = doc(db, REWARDS_COLLECTION, rewardId);
    const rewardDoc = await getDoc(rewardRef);
    
    if (!rewardDoc.exists()) {
      throw new Error('Prêmio não encontrado');
    }

    const reward = rewardDoc.data();
    
    // Se não tem controle de estoque, não há lotes
    if (reward.hasStockControl === false) {
      return [];
    }

    // Se não tem estoque, não há lotes disponíveis
    if (!reward.stockQuantity || reward.stockQuantity <= 0) {
      return [];
    }

    // Por enquanto, retornar o lote atual se existe e tem estoque
    // No futuro, isso pode ser expandido para múltiplos lotes
    const batches = [];
    if (reward.batchCode && reward.stockQuantity > 0) {
      batches.push({
        batchCode: reward.batchCode,
        stockQuantity: reward.stockQuantity,
        rewardId: rewardId,
        rewardName: reward.name
      });
    }

    return batches;
  } catch (error) {
    console.error('Erro ao buscar lotes do prêmio:', error);
    throw new Error('Falha ao buscar lotes: ' + error.message);
  }
};

// Deletar prêmio (soft delete)
export const deleteReward = async (rewardId) => {
  try {
    const rewardRef = doc(db, REWARDS_COLLECTION, rewardId);
    await updateDoc(rewardRef, {
      isActive: false,
      updatedAt: Timestamp.now()
    });
    return true;
  } catch (error) {
    console.error('Erro ao deletar prêmio:', error);
    throw new Error('Falha ao deletar prêmio');
  }
};

// Buscar prêmios disponíveis para um cliente (público)
export const getPublicAvailableRewards = async (companyId, customerPoints) => {
  try {
    if (!companyId) {
      return [];
    }

    // Query pública - só busca prêmios ativos
    const q = query(
      collection(db, REWARDS_COLLECTION),
      where('companyId', '==', companyId),
      where('isActive', '==', true)
    );

    const querySnapshot = await getDocs(q);
    const rewards = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Ordenar por pontos necessários
    rewards.sort((a, b) => a.pointsRequired - b.pointsRequired);
    
    return rewards.map(reward => {
      const hasStock = (reward.type !== 'product' && reward.type !== 'rental') || !reward.hasStockControl || (reward.stockQuantity > 0);
      const isLowStock = (reward.type === 'product' || reward.type === 'rental') && reward.hasStockControl && reward.stockQuantity < reward.lowStockThreshold;
      
      return {
        ...reward,
        canRedeem: customerPoints >= reward.pointsRequired && hasStock,
        pointsNeeded: Math.max(0, reward.pointsRequired - customerPoints),
        // Informações de estoque
        stockQuantity: reward.stockQuantity || 0,
        hasStockControl: (reward.type === 'product' || reward.type === 'rental') ? (reward.hasStockControl !== false) : false,
        isOutOfStock: (reward.type === 'product' || reward.type === 'rental') && reward.hasStockControl && reward.stockQuantity <= 0,
        isLowStock: isLowStock,
        stockStatus: (reward.type !== 'product' && reward.type !== 'rental') ? 'not_applicable' :
                    !reward.hasStockControl ? 'unlimited' : 
                    reward.stockQuantity <= 0 ? 'out_of_stock' :
                    isLowStock ? 'low_stock' : 'available'
      };
    });
  } catch (error) {
    console.error('Erro ao buscar prêmios públicos:', error);
    // Em caso de erro, retorna array vazio em vez de throw
    return [];
  }
};

// Buscar prêmios disponíveis para um cliente (autenticado)
export const getAvailableRewards = async (companyId, customerPoints) => {
  try {
    const rewards = await getCompanyRewards(companyId, true);
    
    return rewards.map(reward => ({
      ...reward,
      canRedeem: customerPoints >= reward.pointsRequired,
      pointsNeeded: Math.max(0, reward.pointsRequired - customerPoints)
    }));
  } catch (error) {
    console.error('Erro ao buscar prêmios disponíveis:', error);
    throw new Error('Falha ao buscar prêmios disponíveis');
  }
};

/**
 * SERVIÇOS DE RESGATES
 */

// Solicitar resgate de pontos
export const requestRedemption = async (customerId, companyId, rewardId, customerPoints) => {
  try {
    // Primeiro, verificar se o prêmio existe e está ativo
    const rewardRef = doc(db, REWARDS_COLLECTION, rewardId);
    const rewardDoc = await getDoc(rewardRef);
    
    if (!rewardDoc.exists()) {
      throw new Error('Prêmio não encontrado');
    }

    const reward = rewardDoc.data();
    
    if (!reward.isActive) {
      throw new Error('Prêmio não está mais disponível');
    }

    if (customerPoints < reward.pointsRequired) {
      throw new Error('Pontos insuficientes para este resgate');
    }

    // Verificar estoque se o prêmio tiver controle de estoque
    if ((reward.type === 'product' || reward.type === 'rental') && reward.hasStockControl !== false) {
      const currentStock = reward.stockQuantity || 0;
      if (currentStock <= 0) {
        throw new Error(`${reward.type === 'rental' ? 'Item' : 'Prêmio'} fora de estoque`);
      }
    }

    // Criar solicitação de resgate
    const redemption = {
      customerId,
      companyId,
      rewardId,
      rewardName: reward.name,
      pointsUsed: reward.pointsRequired,
      status: 'pending', // pending, approved, rejected, completed
      requestedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      // Informações do estoque no momento do resgate
      stockAtRequest: (reward.type === 'product' || reward.type === 'rental') ? (reward.stockQuantity || 0) : 0,
      hasStockControl: (reward.type === 'product' || reward.type === 'rental') ? (reward.hasStockControl !== false) : false
    };

    const docRef = await addDoc(collection(db, REDEMPTIONS_COLLECTION), redemption);
    return { id: docRef.id, ...redemption };
  } catch (error) {
    console.error('Erro ao solicitar resgate:', error);
    throw new Error(error.message || 'Falha ao solicitar resgate');
  }
};

// Buscar resgates de um cliente
export const getCustomerRedemptions = async (customerId) => {
  try {
    const q = query(
      collection(db, REDEMPTIONS_COLLECTION),
      where('customerId', '==', customerId)
    );

    const querySnapshot = await getDocs(q);
    let redemptions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Ordenar por data de solicitação (mais recente primeiro)
    redemptions.sort((a, b) => {
      const dateA = a.requestedAt?.seconds || 0;
      const dateB = b.requestedAt?.seconds || 0;
      return dateB - dateA;
    });

    return redemptions;
  } catch (error) {
    console.error('Erro ao buscar resgates do cliente:', error);
    throw new Error('Falha ao buscar resgates');
  }
};

// Buscar resgates de uma empresa
export const getCompanyRedemptions = async (companyId, status = null) => {
  try {
    // Query básica por companyId
    const q = query(
      collection(db, REDEMPTIONS_COLLECTION),
      where('companyId', '==', companyId)
    );

    const querySnapshot = await getDocs(q);
    let redemptions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Filtrar por status se necessário
    if (status) {
      redemptions = redemptions.filter(redemption => redemption.status === status);
    }

    // Ordenar por data de solicitação (mais recente primeiro)
    redemptions.sort((a, b) => {
      const dateA = a.requestedAt?.seconds || 0;
      const dateB = b.requestedAt?.seconds || 0;
      return dateB - dateA;
    });

    return redemptions;
  } catch (error) {
    console.error('Erro ao buscar resgates da empresa:', error);
    throw new Error('Falha ao buscar resgates');
  }
};

// Atualizar status de resgate (aprovar/rejeitar/completar)
export const updateRedemptionStatus = async (redemptionId, status, notes = '', additionalData = {}) => {
  try {
    const redemptionRef = doc(db, REDEMPTIONS_COLLECTION, redemptionId);
    
    // Primeiro, buscar os dados do resgate para pegar customerId e pointsUsed
    const redemptionDoc = await getDoc(redemptionRef);
    if (!redemptionDoc.exists()) {
      throw new Error('Resgate não encontrado');
    }
    
    const redemptionData = redemptionDoc.data();
    
    const updatedData = {
      status,
      notes,
      updatedAt: Timestamp.now()
    };

    // Adicionar informação do lote selecionado se disponível
    if (additionalData.selectedBatchCode) {
      updatedData.selectedBatchCode = additionalData.selectedBatchCode;
    }

    if (status === 'approved') {
      updatedData.approvedAt = Timestamp.now();
      
      // Na aprovação, apenas validamos se há estoque disponível, mas não reduzimos
      if (redemptionData.rewardId && redemptionData.hasStockControl !== false) {
        const rewardRef = doc(db, REWARDS_COLLECTION, redemptionData.rewardId);
        const rewardDoc = await getDoc(rewardRef);
        
        if (rewardDoc.exists() && (rewardDoc.data().type === 'product' || rewardDoc.data().type === 'rental')) {
          const currentStock = rewardDoc.data().stockQuantity || 0;
          if (currentStock <= 0) {
            throw new Error('Estoque insuficiente para aprovação deste resgate');
          }
        }
      }
      
    } else if (status === 'completed') {
      updatedData.completedAt = Timestamp.now();
      
      // Quando o resgate é marcado como completado, abater os pontos do cliente
      if (redemptionData.customerId && redemptionData.pointsUsed) {
        try {
          // Importar pointsService dinamicamente
          const { pointsService } = await import('./pointsService');
          
          // Criar entrada no histórico de pontos e descontar do cliente
          await pointsService.redeemPoints({
            companyId: redemptionData.companyId,
            customerId: redemptionData.customerId,
            points: redemptionData.pointsUsed,
            type: 'redeem',
            description: `Resgate do prêmio: ${redemptionData.rewardName}`,
            rewardId: redemptionData.rewardId,
            rewardName: redemptionData.rewardName,
            redemptionId: redemptionId,
            batchCode: additionalData.selectedBatchCode || null
          });
          
          // Buscar dados atualizados do cliente e do prêmio
          const updatedCustomer = await customerService.getById(redemptionData.customerId);
          
          // Se for um prêmio de aluguel, criar movimentação de aluguel
          if (redemptionData.rewardId) {
            const rewardRef = doc(db, REWARDS_COLLECTION, redemptionData.rewardId);
            const rewardDoc = await getDoc(rewardRef);
            
            if (rewardDoc.exists()) {
              const rewardData = rewardDoc.data();
              
              if (rewardData.type === 'rental') {
                try {
                  const { createRentalMovement } = await import('./rentalService');
                  
                  // Calcular datas automaticamente baseado na data atual e duração padrão
                  const today = new Date();
                  const deliveryDate = today; // Data atual como data de entrega
                  const expectedReturnDate = new Date(today);
                  expectedReturnDate.setDate(expectedReturnDate.getDate() + (rewardData.rentalDuration || 7));

                  const rentalMovementData = {
                    rewardId: redemptionData.rewardId,
                    rewardName: rewardData.name,
                    customerId: redemptionData.customerId,
                    customerName: updatedCustomer.name,
                    customerDocument: updatedCustomer.cpf || updatedCustomer.cnpj,
                    rentalDuration: rewardData.rentalDuration || 7,
                    expectedDeliveryDate: deliveryDate.toISOString().split('T')[0], // Data atual
                    actualDeliveryDate: deliveryDate.toISOString().split('T')[0], // Data atual como entrega
                    expectedReturnDate: expectedReturnDate.toISOString().split('T')[0],
                    pointsUsed: redemptionData.pointsUsed,
                    notes: notes || `Aluguel iniciado via resgate forçado - ${redemptionId}`
                  };

                  await createRentalMovement(redemptionData.companyId, rentalMovementData);
                } catch (rentalError) {
                  console.error('❌ Erro ao criar movimentação de aluguel:', rentalError);
                  // Não falhar o resgate se houver erro na movimentação
                }
              }
              
              // Reduzir estoque para produtos e aluguéis com controle de estoque
              if ((rewardData.type === 'product' || rewardData.type === 'rental') && rewardData.hasStockControl !== false) {
                try {
                  await reduceRewardStock(
                    redemptionData.rewardId, 
                    1, 
                    redemptionId, 
                    updatedCustomer?.name,
                    additionalData.selectedBatchCode || null
                  );
                } catch (stockError) {
                  console.error('Erro ao reduzir estoque:', stockError);
                  throw new Error('Erro ao processar resgate: ' + stockError.message);
                }
              }
            }
          }
          
          // Enviar SMS de prêmio resgatado (não bloquear em caso de erro)
          try {
            const smsService = await import('./smsService');
            await smsService.default.sendRewardRedeemedSMS(
              redemptionData.companyId, 
              updatedCustomer, 
              redemptionData.rewardName || 'Prêmio', 
              updatedCustomer.points || 0
            );
          } catch (smsError) {
            console.warn('Erro ao enviar SMS (não crítico):', smsError);
            // Não falhar a operação se SMS falhar
          }
          
        } catch (customerError) {
          console.error('Erro ao abater pontos do cliente:', customerError);
          throw new Error('Erro ao processar resgate: não foi possível abater os pontos do cliente');
        }
      }
    } else if (status === 'rejected') {
      updatedData.rejectedAt = Timestamp.now();
      
      // Se foi rejeitado após aprovação, devolver estoque para produtos e aluguéis
      if (redemptionData.status === 'approved' && redemptionData.rewardId && redemptionData.hasStockControl !== false) {
        // Verificar se é produto ou aluguel primeiro
        const rewardRef = doc(db, REWARDS_COLLECTION, redemptionData.rewardId);
        const rewardDoc = await getDoc(rewardRef);
        
        if (rewardDoc.exists() && (rewardDoc.data().type === 'product' || rewardDoc.data().type === 'rental')) {
          try {
            await addRewardStock(redemptionData.rewardId, 1);
          } catch (stockError) {
            console.warn('Erro ao devolver estoque (não crítico):', stockError);
            // Não falhar a operação se não conseguir devolver estoque
          }
        }
      }
    }

    await updateDoc(redemptionRef, updatedData);
    return { id: redemptionId, ...updatedData };
  } catch (error) {
    console.error('Erro ao atualizar status do resgate:', error);
    throw new Error('Falha ao atualizar resgate');
  }
};

// Buscar detalhes completos de um resgate (com dados do cliente e prêmio)
export const getRedemptionDetails = async (redemptionId) => {
  try {
    const redemptionRef = doc(db, REDEMPTIONS_COLLECTION, redemptionId);
    const redemptionDoc = await getDoc(redemptionRef);
    
    if (!redemptionDoc.exists()) {
      throw new Error('Resgate não encontrado');
    }

    return {
      id: redemptionDoc.id,
      ...redemptionDoc.data()
    };
  } catch (error) {
    console.error('Erro ao buscar detalhes do resgate:', error);
    throw new Error('Falha ao buscar detalhes do resgate');
  }
};

// Criar resgate manual (sem prêmio específico)
export const createManualRedemption = async (customerId, companyId, pointsUsed, description, notes = '') => {
  try {
    // Buscar dados do cliente antes da operação
    const customer = await customerService.getById(customerId);
    if (!customer) {
      throw new Error('Cliente não encontrado');
    }
    
    // Criar solicitação de resgate manual
    const redemption = {
      customerId,
      companyId,
      rewardId: null, // Sem prêmio específico
      rewardName: 'Resgate Manual',
      pointsUsed: parseInt(pointsUsed),
      status: 'completed', // Já marcar como concluído pois foi forçado
      requestedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      completedAt: Timestamp.now(),
      notes: `Resgate forçado pela empresa. ${description}. ${notes}`.trim(),
      type: 'manual'
    };

    const docRef = await addDoc(collection(db, REDEMPTIONS_COLLECTION), redemption);
    
    // Registrar a movimentação de pontos no histórico
    try {
      const { pointsService } = await import('./pointsService');
      
      await pointsService.redeemPoints({
        companyId: companyId,
        customerId: customerId,
        points: parseInt(pointsUsed),
        type: 'redeem',
        description: `Resgate manual: ${description}`,
        rewardId: null,
        rewardName: 'Resgate Manual',
        redemptionId: docRef.id,
        batchCode: null
      });
    } catch (pointsError) {
      console.error('Erro ao registrar pontos do resgate manual:', pointsError);
      // Não falhar a operação principal se houver erro no registro de pontos
    }
    
    return { id: docRef.id, ...redemption };
  } catch (error) {
    console.error('Erro ao criar resgate manual:', error);
    throw new Error(error.message || 'Falha ao criar resgate manual');
  }
};
